/**
 * AES-ECB-128 加密工具 (纯 JS 实现，兼容所有浏览器)
 * 与 CryptoJS AES-ECB 结果兼容
 */

// AES S-Box
const SBOX = new Uint8Array([
  0x63, 0x7c, 0x77, 0x7b, 0xf2, 0x6b, 0x6f, 0xc5, 0x30, 0x01, 0x67, 0x2b, 0xfe, 0xd7, 0xab, 0x76,
  0xca, 0x82, 0xc9, 0x7d, 0xfa, 0x59, 0x47, 0xf0, 0xad, 0xd4, 0xa2, 0xaf, 0x9c, 0xa4, 0x72, 0xc0,
  0xb7, 0xfd, 0x93, 0x26, 0x36, 0x3f, 0xf7, 0xcc, 0x34, 0xa5, 0xe5, 0xf1, 0x71, 0xd8, 0x31, 0x15,
  0x04, 0xc7, 0x23, 0xc3, 0x18, 0x96, 0x05, 0x9a, 0x07, 0x12, 0x80, 0xe2, 0xeb, 0x27, 0xb2, 0x75,
  0x09, 0x83, 0x2c, 0x1a, 0x1b, 0x6e, 0x5a, 0xa0, 0x52, 0x3b, 0xd6, 0xb3, 0x29, 0xe3, 0x2f, 0x84,
  0x53, 0xd1, 0x00, 0xed, 0x20, 0xfc, 0xb1, 0x5b, 0x6a, 0xcb, 0xbe, 0x39, 0x4a, 0x4c, 0x58, 0xcf,
  0xd0, 0xef, 0xaa, 0xfb, 0x43, 0x4d, 0x33, 0x85, 0x45, 0xf9, 0x02, 0x7f, 0x50, 0x3c, 0x9f, 0xa8,
  0x51, 0xa3, 0x40, 0x8f, 0x92, 0x9d, 0x38, 0xf5, 0xbc, 0xb6, 0xda, 0x21, 0x10, 0xff, 0xf3, 0xd2,
  0xcd, 0x0c, 0x13, 0xec, 0x5f, 0x97, 0x44, 0x17, 0xc4, 0xa7, 0x7e, 0x3d, 0x64, 0x5d, 0x19, 0x73,
  0x60, 0x81, 0x4f, 0xdc, 0x22, 0x2a, 0x90, 0x88, 0x46, 0xee, 0xb8, 0x14, 0xde, 0x5e, 0x0b, 0xdb,
  0xe0, 0x32, 0x3a, 0x0a, 0x49, 0x06, 0x24, 0x5c, 0xc2, 0xd3, 0xac, 0x62, 0x91, 0x95, 0xe4, 0x79,
  0xe7, 0xc8, 0x37, 0x6d, 0x8d, 0xd5, 0x4e, 0xa9, 0x6c, 0x56, 0xf4, 0xea, 0x65, 0x7a, 0xae, 0x08,
  0xba, 0x78, 0x25, 0x2e, 0x1c, 0xa6, 0xb4, 0xc6, 0xe8, 0xdd, 0x74, 0x1f, 0x4b, 0xbd, 0x8b, 0x8a,
  0x70, 0x3e, 0xb5, 0x66, 0x48, 0x03, 0xf6, 0x0e, 0x61, 0x35, 0x57, 0xb9, 0x86, 0xc1, 0x1d, 0x9e,
  0xe1, 0xf8, 0x98, 0x11, 0x69, 0xd9, 0x8e, 0x94, 0x9b, 0x1e, 0x87, 0xe9, 0xce, 0x55, 0x28, 0xdf,
  0x8c, 0xa1, 0x89, 0x0d, 0xbf, 0xe6, 0x42, 0x68, 0x41, 0x99, 0x2d, 0x0f, 0xb0, 0x54, 0xbb, 0x16
])

const RCON = new Uint8Array([0x01, 0x02, 0x04, 0x08, 0x10, 0x20, 0x40, 0x80, 0x1b, 0x36])

// 字符串转 UTF-8 字节数组
function stringToBytes(str) {
  const encoder = new TextEncoder()
  return encoder.encode(str)
}

// 字节数组转 Base64
function bytesToBase64(bytes) {
  let binary = ''
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i])
  }
  return btoa(binary)
}

// 密钥扩展 - AES-128 需要 44 个 32 位字 (176 字节)
function keyExpansion(key) {
  const w = new Uint8Array(176)
  w.set(key)

  let i = 16
  let rconIdx = 0

  while (i < 176) {
    // 取前 4 字节
    let temp = [w[i - 3], w[i - 2], w[i - 1], w[i - 4]]

    // 每 16 字节执行一次 RotWord 和 SubWord
    temp = [SBOX[temp[0]] ^ RCON[rconIdx], SBOX[temp[1]], SBOX[temp[2]], SBOX[temp[3]]]
    rconIdx++

    for (let j = 0; j < 4; j++) {
      w[i] = w[i - 16] ^ temp[j]
      i++
    }

    // 接下来 3 轮只做 XOR
    for (let k = 0; k < 3; k++) {
      for (let j = 0; j < 4; j++) {
        w[i] = w[i - 16] ^ w[i - 4]
        i++
      }
    }
  }

  return w
}

// SubBytes
function subBytes(state) {
  for (let i = 0; i < 16; i++) {
    state[i] = SBOX[state[i]]
  }
}

// ShiftRows
function shiftRows(state) {
  let t
  // Row 1: shift left by 1
  t = state[1]; state[1] = state[5]; state[5] = state[9]; state[9] = state[13]; state[13] = t
  // Row 2: shift left by 2
  t = state[2]; state[2] = state[10]; state[10] = t
  t = state[6]; state[6] = state[14]; state[14] = t
  // Row 3: shift left by 3
  t = state[15]; state[15] = state[11]; state[11] = state[7]; state[7] = state[3]; state[3] = t
}

// xtime - GF(2^8) 乘以 2
function xtime(x) {
  return ((x << 1) ^ (((x >> 7) & 1) * 0x1b)) & 0xff
}

// MixColumns
function mixColumns(state) {
  for (let c = 0; c < 4; c++) {
    const i = c * 4
    const a = state[i], b = state[i + 1], cc = state[i + 2], d = state[i + 3]
    state[i]     = xtime(a) ^ xtime(b) ^ b ^ cc ^ d
    state[i + 1] = a ^ xtime(b) ^ xtime(cc) ^ cc ^ d
    state[i + 2] = a ^ b ^ xtime(cc) ^ xtime(d) ^ d
    state[i + 3] = xtime(a) ^ a ^ b ^ cc ^ xtime(d)
  }
}

// AddRoundKey
function addRoundKey(state, key) {
  for (let i = 0; i < 16; i++) {
    state[i] ^= key[i]
  }
}

// AES-128 加密单个 16 字节块
function encryptBlock(block, expandedKey) {
  const state = new Uint8Array(block)

  // 初始轮
  addRoundKey(state, expandedKey.slice(0, 16))

  // 9 轮主循环
  for (let round = 1; round <= 9; round++) {
    subBytes(state)
    shiftRows(state)
    mixColumns(state)
    addRoundKey(state, expandedKey.slice(round * 16, (round + 1) * 16))
  }

  // 最终轮 (无 MixColumns)
  subBytes(state)
  shiftRows(state)
  addRoundKey(state, expandedKey.slice(160, 176))

  return state
}

// PKCS7 填充
function pkcs7Pad(data) {
  const padLen = 16 - (data.length % 16)
  const result = new Uint8Array(data.length + padLen)
  result.set(data)
  for (let i = data.length; i < result.length; i++) {
    result[i] = padLen
  }
  return result
}

/**
 * AES-ECB-128 加密
 * @param {string} plaintext - 要加密的内容
 * @param {string} keyStr - 密钥 (取前16字节)
 * @returns {string} Base64 编码的加密结果
 */
export function aesEncrypt(plaintext, keyStr) {
  // 准备密钥 (16 字节)
  const keyBytes = stringToBytes(keyStr)
  const key = new Uint8Array(16)
  key.set(keyBytes.slice(0, 16))

  // 扩展密钥
  const expandedKey = keyExpansion(key)

  // 准备明文
  const plainBytes = stringToBytes(plaintext)
  const paddedData = pkcs7Pad(plainBytes)

  // 加密
  const result = new Uint8Array(paddedData.length)
  for (let i = 0; i < paddedData.length; i += 16) {
    const block = paddedData.slice(i, i + 16)
    const encrypted = encryptBlock(block, expandedKey)
    result.set(encrypted, i)
  }

  return bytesToBase64(result)
}